<template>
  <div>
    <div class="card-item p-0">
      <RelatedPaymentsHeader
        v-if="hasSiblings"
        :current-user-is-client="currentUserIsClient"
        :payments-status="paymentsStatus"
        :related-payment-id="relatedPaymentId"
        :disabled="(
          allCheckBoxesAreDisabled ||
          (paymentsStatus.paymentsAreNotTheFirst
            && !paymentsStatus.arePreviousPaymentsInPaymentsMap)
        )"
        :payments="payments"
        :payment-index="paymentIndex"
        :global-extended-payment="globalExtendedPayment"
        @change="$emit('change', $event)"
        @extend-payment="$emit('extend-payment')" />
      <div
        class="transition-fast"
        :class="[
          hasSiblings
            ? (globalExtendedPayment === relatedPaymentId
              && (paymentsStatus.anyPaymentsInPaymentsMap
                || paymentsStatus.somePaymentsArePending)
            ) ? 'show' : 'hide'
            : ''
        ]">
        <div
          v-for="payment in payments"
          :id="`payment-${payment.operationId}-${payment.paymentNumber}`"
          :key="`payment-${payment.operationId}-${payment.paymentNumber}`"
          :class="{ 'm-5': hasSiblings }">
          <SinglePayment
            :id="`id-${payment.operationId}-${payment.paymentNumber}`"
            :class="{ extended: extendedPayment === `${payment.operationId}-${payment.paymentNumber}` }"
            :style="`z-index: ${extendedPayment === `${payment.operationId}-${payment.paymentNumber}` ? 20 : 'auto'}`"
            :current-user-is-client="currentUserIsClient"
            :collect-client-debt="collectClientDebt"
            :payment="payment"
            :operation="operation"
            :type="payment.type"
            :has-siblings="hasSiblings"
            :checked="paymentsMap[payment.operationId] && paymentsMap[payment.operationId].some(p => p.paymentNumber === payment.paymentNumber)"
            :is-payment-loading="currentLoadingPayment === `${payment.operationId}-${payment.paymentNumber}`"
            :show-remove-late-charges="showRemoveLateCharges"
            :disabled="(
              (allCheckBoxesAreDisabled && currentLoadingPayment !== `${payment.operationId}-${payment.paymentNumber}`) ||
              (
                // es el primer pago de la operación a la que corresponde
                payment.paymentIndex > 0 &&
                !(
                  // existe la operación en el mapa
                  paymentsMap[payment.operationId] &&
                  // existe la cuota anterior en el mapa
                  paymentsMap[payment.operationId][payment.paymentIndex-1] &&
                  // la cuota anterior + 1 es igual a la cuota actual
                  paymentsMap[payment.operationId][payment.paymentIndex-1].paymentNumber + 1 === payment.paymentNumber
                )
              )
            )"
            :is-extended="(
              // Si no hay más payments y la cuota extendida en clientOps es la actual
              !hasSiblings && globalExtendedPayment === `${payment.operationId}-${payment.paymentNumber}`
              // O, si hay más de un payment y el actual está extendido
              || hasSiblings && extendedPayment === `${payment.operationId}-${payment.paymentNumber}`
            )"
            @confirm-deselect-payment="$emit('confirm-deselect-payment', $event)"
            @extend-payment="extendPayment(`${payment.operationId}-${payment.paymentNumber}`)"
            @remove-late-charges="removeLateCharges($event, extendedPayment)"
            @change="$emit('change', $event)"
            @undo-remove-late-charges="undoRemoveLateCharges($event, extendedPayment)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @confirmDeselectPayment="$emit('change', $event), extendedPayment === `${payment.operationId}-${payment.paymentNumber}` ? extendedPayment = '' : ''"
import SinglePayment from '@/ui/components/internal/pay-here/SinglePayment.vue';
import RelatedPaymentsHeader from '@/ui/components/internal/pay-here/RelatedPaymentsHeader.vue';

export default {
  components: { SinglePayment, RelatedPaymentsHeader },
  props: {
    paymentsObject: { type: Object, default: () => ({}) },
    operation: { type: Object, default: () => ({}) },
    paymentsMap: { type: Object, default: () => ({}) },
    relatedPaymentId: { type: String, default: '' },
    paymentIndex: { type: Number, default: 0 },
    globalExtendedPayment: { type: String, default: '' },
    currentLoadingPayment: { type: String, default: '' },
    showRemoveLateCharges: { type: Boolean, default: false },
    currentUserIsClient: { type: Boolean, default: false },
    allCheckBoxesAreDisabled: { type: Boolean, default: false },
    collectClientDebt: { type: Boolean, default: false }
  },
  data() {
    return {
      extendedPayment: ''
    };
  },
  computed: {
    payments() {
      return Object.values(this.paymentsObject);
    },
    hasSiblings() {
      return this.payments.length > 1;
    },
    paymentsStatus() {
      return this.payments.reduce((acc, payment) => {
        acc.paymentsAreLoading = `${payment.operationId}-${payment.paymentNumber}` === this.currentLoadingPayment || acc.paymentsAreLoading;
        const isPaymentInPaymentsMap = this.paymentsMap[payment.operationId] && this.paymentsMap[payment.operationId].some(p => p.paymentNumber === payment.paymentNumber);
        if (isPaymentInPaymentsMap) {
          acc.totalAmount += payment.secondaryCurrencyTotalMonthlyPayment;
        }
        const isPreviousPaymentInPaymentsMap = (!!this.paymentsMap[payment.operationId]
            && !!this.paymentsMap[payment.operationId][payment.paymentIndex - 1]
            && this.paymentsMap[payment.operationId][payment.paymentIndex - 1].paymentNumber + 1 === payment.paymentNumber);

        if (payment.isPending) {
          acc.somePaymentsArePending = true;
          acc.totalTransactionPaymentsAmount += payment.secondaryCurrencyTotalMonthlyPayment;
        } else {
          acc.somePaymentsAreNotPending = true;
        }
        acc.arePreviousPaymentsInPaymentsMap = acc.arePreviousPaymentsInPaymentsMap || isPreviousPaymentInPaymentsMap;
        acc.anyPaymentsInPaymentsMap = acc.anyPaymentsInPaymentsMap || isPaymentInPaymentsMap;
        if (payment.paymentIndex === 0) { acc.paymentsAreNotTheFirst = false; }
        if (!payment.isPending && (isPreviousPaymentInPaymentsMap || payment.paymentIndex === 0)) {
          if (!isPaymentInPaymentsMap) {
            const selectablePayment = {
              type: payment.type,
              paymentNumber: payment.paymentNumber,
              operationId: payment.operationId
            };
            acc.selectablePayments.push(selectablePayment);
          } else {
            const unselectablePayment = {
              type: payment.type,
              paymentNumber: payment.paymentNumber,
              operationId: payment.operationId
            };
            acc.unselectablePayments.push(unselectablePayment);
          }
        }
        return acc;
      },
      {
        somePaymentsArePending: false,
        somePaymentsAreNotPending: false,
        paymentsAreLoading: false,
        totalAmount: 0,
        totalTransactionPaymentsAmount: 0,
        arePreviousPaymentsInPaymentsMap: false,
        anyPaymentsInPaymentsMap: false,
        paymentsAreNotTheFirst: true,
        selectablePayments: [],
        unselectablePayments: []
      });
    }
  },
  methods: {
    removeLateCharges(e, extendedPayment) {
      e.extendedPayment = extendedPayment;
      this.$emit('remove-late-charges', e);
    },
    undoRemoveLateCharges(e, extendedPayment) {
      e.extendedPayment = extendedPayment;
      this.$emit('undo-remove-late-charges', e);
    },
    extendPayment(paymentId) {
      // Si hay más de un payment y la cuota extendida en clientOps es la actual, lo extiende
      if (this.hasSiblings) {
        this.extendedPayment = this.extendedPayment !== paymentId ? paymentId : '';
        return;
      }
      // Si no, emite para que clientsOps haga lo suyo
      this.$emit('extend-payment', paymentId);
    }
  }
};
</script>

<style>
.card-item {
  overflow: unset;
}
.hide {
  max-height: 0;
  overflow: hidden;
  padding: 0;
}
.show {
  max-height: 100rem;
  overflow: hidden;
}
</style>
